import { useCallback, useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { Permission, PermissionEntitiesNames } from '@fabric-ui/components';
import { EntityProjectionName, entityProjectionNames } from '@utils/entityProjectionNames';
import { SettingType } from '@views/Settings/hooks';
import { ProjectionToRiskScore } from './types';

type RiskScoreProjectionConfig = { permissionResource: PermissionEntitiesNames; settingType: SettingType };

const RISK_SCORE_PROJECTION_CONFIG: Partial<Record<EntityProjectionName, RiskScoreProjectionConfig>> = {
  [entityProjectionNames.Asset]: {
    permissionResource: PermissionEntitiesNames.ASSET_RISK_SCORE,
    settingType: SettingType.ASSET_RISK_SCORE,
  },
  [entityProjectionNames.Finding]: {
    permissionResource: PermissionEntitiesNames.FINDING_RISK_SCORE,
    settingType: SettingType.FINDING_RISK_SCORE,
  },
};

export const getRiskScoreProjectionConfig = (projectionName: EntityProjectionName): RiskScoreProjectionConfig =>
  RISK_SCORE_PROJECTION_CONFIG[projectionName]!;

export const useCanReadAnyRiskScore = (): boolean => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();

  return Object.values(RISK_SCORE_PROJECTION_CONFIG).some(
    ({ permissionResource }) =>
      hasAllowedPermissionToResource({ resource: permissionResource, permission: Permission.READ }) ||
      hasAllowedPermissionToResource({ resource: permissionResource, permission: Permission.UPDATE })
  );
};

export interface RiskScorePermissions {
  canRead: boolean;
  canUpdate: boolean;
  canUpdateBaseScore: (baseScoreProjectionName: string) => boolean;
}

export const useRiskScorePermissions = (
  riskScoreProjectionName: EntityProjectionName,
  projectionToRiskScores: ProjectionToRiskScore[] = []
): RiskScorePermissions => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();

  const permissionResource = getRiskScoreProjectionConfig(riskScoreProjectionName)?.permissionResource;

  const canUpdate = useMemo(
    () => !!permissionResource && hasAllowedPermissionToResource({ resource: permissionResource, permission: Permission.UPDATE }),
    [permissionResource, hasAllowedPermissionToResource]
  );

  const canRead = useMemo(
    () =>
      canUpdate || (!!permissionResource && hasAllowedPermissionToResource({ resource: permissionResource, permission: Permission.READ })),
    [canUpdate, permissionResource, hasAllowedPermissionToResource]
  );

  const canUpdateBaseScore = useCallback(
    (baseScoreProjectionName: string) =>
      projectionToRiskScores.some(({ projectionName, riskScore }) => {
        const resource = getRiskScoreProjectionConfig(projectionName as EntityProjectionName)?.permissionResource;
        return (
          !!resource &&
          riskScore.config.baseScoreWeightContribution.some(bs => bs.projectionName === baseScoreProjectionName) &&
          hasAllowedPermissionToResource({ resource, permission: Permission.UPDATE })
        );
      }),
    [projectionToRiskScores, hasAllowedPermissionToResource]
  );

  return { canRead, canUpdate, canUpdateBaseScore };
};
